import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Checkbox } from '../../components/ui/Checkbox';
import { Search, ClipboardList, SearchCode, User, Phone, ArrowLeft, CheckCircle2, Package, AlertCircle, ArrowRight, Pill } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useToast } from '../../components/ui/Toast';
interface PrescriptionItem {
  id: string;
  name: string;
  dosage: string;
  quantity: number;
  inStock: boolean;
}
interface PrescriptionData {
  id: string;
  episodeId: string;
  patientName: string;
  nationalId: string;
  phone: string;
  insurance: string;
  items: PrescriptionItem[];
}
const MOCK_PRESCRIPTION: PrescriptionData = {
  id: 'pres-123',
  episodeId: 'EP-2026-001',
  patientName: 'Ahmed Mansour',
  nationalId: '1098765432',
  phone: '+966 50 111 2222',
  insurance: 'Tawuniya - Class A',
  items: [{
    id: 'item-1',
    name: 'Panadol Advance 500mg',
    dosage: '1 tablet every 6 hours',
    quantity: 2,
    inStock: true
  }, {
    id: 'item-2',
    name: 'Amoxicillin 500mg',
    dosage: '1 capsule 3 times daily',
    quantity: 1,
    inStock: true
  }, {
    id: 'item-3',
    name: 'Nexium 40mg',
    dosage: '1 tablet daily before breakfast',
    quantity: 1,
    inStock: false
  }, {
    id: 'item-4',
    name: 'Ventolin Inhaler',
    dosage: '2 puffs as needed',
    quantity: 1,
    inStock: true
  }]
};
export function Referrals() {
  const {
    showToast
  } = useToast();
  const [step, setStep] = useState<'search' | 'details' | 'success'>('search');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [nationalId, setNationalId] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [prescription, setPrescription] = useState<PrescriptionData | null>(null);
  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nationalId || !phone) return;
    setIsLookingUp(true);
    // Simulate API call to Tawuniya/Waseel
    await new Promise(resolve => setTimeout(resolve, 1500));
    setPrescription(MOCK_PRESCRIPTION);
    setIsLookingUp(false);
    setStep('details');
    showToast({
      type: 'success',
      title: 'Prescription Found',
      message: `Retrieved prescription for ${MOCK_PRESCRIPTION.patientName}`
    });
  };
  const toggleItem = (itemId: string) => {
    setSelectedItems(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]);
  };
  const handleFulfill = async () => {
    if (selectedItems.length === 0) {
      showToast({
        type: 'error',
        title: 'No Items Selected',
        message: 'Please select at least one item to fulfill.'
      });
      return;
    }
    setIsLookingUp(true);
    // Simulate fulfillment process
    await new Promise(resolve => setTimeout(resolve, 1200));
    setIsLookingUp(false);
    setStep('success');
    showToast({
      type: 'success',
      title: 'Referral Fulfilled',
      message: `Successfully fulfilled ${selectedItems.length} items.`
    });
  };
  const resetFlow = () => {
    setStep('search');
    setNationalId('');
    setPhone('');
    setSelectedItems([]);
    setPrescription(null);
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.4,
    ease: 'easeOut'
  }} className="max-w-4xl mx-auto py-8 px-4" data-id="element-821">
      {/* Header */}
      <div className="mb-8 text-center" data-id="element-822">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-4" data-id="element-823">
          <ClipboardList className="w-8 h-8" data-id="element-824" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900" data-id="element-825">
          Pharmacy Referrals
        </h1>
        <p className="text-slate-500 mt-2" data-id="element-826">
          Search and fulfill insurance prescriptions from other pharmacies
        </p>
      </div>

      <AnimatePresence mode="wait" data-id="element-827">
        {step === 'search' && <motion.div key="search" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="max-w-md mx-auto" data-id="element-828">
            <Card className="border-blue-100 shadow-xl shadow-blue-500/5" data-id="element-829">
              <CardHeader className="text-center border-b border-slate-50 pb-6" data-id="element-830">
                <CardTitle className="text-xl" data-id="element-831">Prescription Lookup</CardTitle>
                <p className="text-sm text-slate-500" data-id="element-832">
                  Enter patient details to retrieve the referral prescription
                </p>
              </CardHeader>
              <CardContent className="pt-8" data-id="element-833">
                <form onSubmit={handleLookup} className="space-y-6" data-id="element-834">
                  <Input label="National ID / Iqama" placeholder="e.g. 10xxxxxxxx" value={nationalId} onChange={e => setNationalId(e.target.value)} icon={<User className="w-4 h-4" data-id="element-836" />} required disabled={isLookingUp} data-id="element-835" />

                  <Input label="Phone Number" placeholder="e.g. +966 5x xxx xxxx" value={phone} onChange={e => setPhone(e.target.value)} icon={<Phone className="w-4 h-4" data-id="element-838" />} required disabled={isLookingUp} data-id="element-837" />

                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg" isLoading={isLookingUp} rightIcon={!isLookingUp && <SearchCode className="w-5 h-5" data-id="element-840" />} data-id="element-839">
                    Search Prescription
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>}

        {step === 'details' && prescription && <motion.div key="details" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="space-y-6" data-id="element-841">
            <div className="flex items-center justify-between" data-id="element-842">
              <Button variant="ghost" onClick={() => setStep('search')} className="text-slate-500 hover:text-slate-900" data-id="element-843">
                <ArrowLeft className="w-4 h-4 mr-2" data-id="element-844" />
                Back to Search
              </Button>
              <Badge variant="info" className="px-3 py-1" data-id="element-845">
                {prescription.episodeId}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-846">
              {/* Patient Info Card */}
              <Card className="md:col-span-1 h-fit" data-id="element-847">
                <CardHeader className="pb-4" data-id="element-848">
                  <CardTitle className="text-base flex items-center gap-2" data-id="element-849">
                    <User className="w-4 h-4 text-blue-500" data-id="element-850" />
                    Patient Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4" data-id="element-851">
                  <div data-id="element-852">
                    <p className="text-xs text-slate-500 uppercase font-bold tracking-wider" data-id="element-853">
                      Name
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-854">
                      {prescription.patientName}
                    </p>
                  </div>
                  <div data-id="element-855">
                    <p className="text-xs text-slate-500 uppercase font-bold tracking-wider" data-id="element-856">
                      National ID
                    </p>
                    <p className="text-sm font-mono text-slate-900" data-id="element-857">
                      {prescription.nationalId}
                    </p>
                  </div>
                  <div data-id="element-858">
                    <p className="text-xs text-slate-500 uppercase font-bold tracking-wider" data-id="element-859">
                      Phone
                    </p>
                    <p className="text-sm font-mono text-slate-900" data-id="element-860">
                      {prescription.phone}
                    </p>
                  </div>
                  <div className="pt-4 border-t border-slate-100" data-id="element-861">
                    <p className="text-xs text-slate-500 uppercase font-bold tracking-wider" data-id="element-862">
                      Insurance
                    </p>
                    <p className="text-sm font-medium text-blue-700" data-id="element-863">
                      {prescription.insurance}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Items Selection Card */}
              <Card className="md:col-span-2" data-id="element-864">
                <CardHeader className="border-b border-slate-50" data-id="element-865">
                  <div className="flex items-center justify-between" data-id="element-866">
                    <CardTitle className="text-base flex items-center gap-2" data-id="element-867">
                      <Pill className="w-4 h-4 text-blue-500" data-id="element-868" />
                      Prescription Items
                    </CardTitle>
                    <span className="text-xs text-slate-500" data-id="element-869">
                      Select items you can fulfill
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="p-0" data-id="element-870">
                  <div className="divide-y divide-slate-100" data-id="element-871">
                    {prescription.items.map(item => <div key={item.id} className={cn('p-4 flex items-center gap-4 transition-colors', selectedItems.includes(item.id) ? 'bg-blue-50/50' : 'hover:bg-slate-50/50')} data-id="element-872">
                        <Checkbox id={item.id} checked={selectedItems.includes(item.id)} onCheckedChange={() => toggleItem(item.id)} disabled={!item.inStock} data-id="element-873" />
                        <div className="flex-1 min-w-0" data-id="element-874">
                          <div className="flex items-center gap-2" data-id="element-875">
                            <p className={cn('font-medium text-slate-900 truncate', !item.inStock && 'text-slate-400')} data-id="element-876">
                              {item.name}
                            </p>
                            {!item.inStock && <Badge variant="neutral" size="sm" className="bg-slate-100 text-slate-500 border-slate-200" data-id="element-877">
                                Out of Stock
                              </Badge>}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5" data-id="element-878">
                            {item.dosage} • Qty: {item.quantity}
                          </p>
                        </div>
                        {item.inStock && <Badge variant="success" size="sm" className="bg-emerald-50 text-emerald-700 border-emerald-100" data-id="element-879">
                            In Stock
                          </Badge>}
                      </div>)}
                  </div>

                  <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-880">
                    <div className="text-sm text-slate-600" data-id="element-881">
                      <span className="font-bold text-blue-600" data-id="element-882">
                        {selectedItems.length}
                      </span>{' '}
                      of{' '}
                      <span className="font-bold" data-id="element-883">
                        {prescription.items.filter(i => i.inStock).length}
                      </span>{' '}
                      available items selected
                    </div>
                    <Button onClick={handleFulfill} disabled={selectedItems.length === 0 || isLookingUp} className="bg-blue-600 hover:bg-blue-700 px-8" isLoading={isLookingUp} data-id="element-884">
                      Fulfill Selected Items
                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-885" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>}

        {step === 'success' && <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="max-w-md mx-auto text-center" data-id="element-886">
            <Card className="border-emerald-100 shadow-xl shadow-emerald-500/5" data-id="element-887">
              <CardContent className="pt-12 pb-8" data-id="element-888">
                <div className="w-20 h-20 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-6" data-id="element-889">
                  <CheckCircle2 className="w-10 h-10" data-id="element-890" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-891">
                  Fulfillment Complete!
                </h2>
                <p className="text-slate-500 mt-2 mb-8" data-id="element-892">
                  The referral items have been successfully processed and
                  updated in the system.
                </p>

                <div className="bg-slate-50 rounded-lg p-4 mb-8 text-left border border-slate-100" data-id="element-893">
                  <div className="flex items-center justify-between text-sm mb-2" data-id="element-894">
                    <span className="text-slate-500" data-id="element-895">Episode ID:</span>
                    <span className="font-mono font-bold text-slate-900" data-id="element-896">
                      {prescription?.episodeId}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm" data-id="element-897">
                    <span className="text-slate-500" data-id="element-898">Items Fulfilled:</span>
                    <span className="font-bold text-emerald-600" data-id="element-899">
                      {selectedItems.length} items
                    </span>
                  </div>
                </div>

                <Button variant="outline" onClick={resetFlow} className="w-full h-12" data-id="element-900">
                  Start New Referral
                </Button>
              </CardContent>
            </Card>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}